// 权益订单 — bundle_orders 对账 + 银商退款 (phase1-promo, 2026-08-13)。
// 与「订单」页(commerce.orders, SKU 收单)分开: 两张互不相关的表。

import { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { listApps, type App } from '@/api/apps';
import { listBundleOrders, refundBundleOrder, type BundleOrderRow } from '@/api/bundles';
import { formatCNY, formatDateTime } from '@/utils/format';

const { Text } = Typography;

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  pending: { color: 'orange', label: '待支付' },
  paid: { color: 'green', label: '已支付' },
  failed: { color: 'default', label: '失败' },
  refunded: { color: 'red', label: '已退款' },
};

export function BenefitOrdersPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<string>('');
  const [rows, setRows] = useState<BundleOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refunding, setRefunding] = useState<string | null>(null);

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((r) => {
        setApps(r.data);
        const first = r.data.find((a) => a.status === 'active') ?? r.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch(() => message.error('加载 App 列表失败'));
  }, []);

  const load = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    listBundleOrders({ app_id: appId, status: status || undefined, page, page_size: 20 })
      .then((r) => {
        setRows(r.data);
        setTotal(r.page?.total ?? r.data.length);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [appId, status, page]);
  useEffect(load, [load]);

  const doRefund = (o: BundleOrderRow) => {
    setRefunding(o.out_trade_no);
    refundBundleOrder(o.out_trade_no)
      .then(() => {
        message.success(`已退款 ${formatCNY(o.amount_cents)} (${o.out_trade_no})`);
        load();
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '退款失败'))
      .finally(() => setRefunding(null));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Text strong style={{ fontSize: 16 }}>权益订单</Text>
        <Select
          style={{ width: 240 }}
          value={appId}
          options={apps.map((a) => ({ value: a.id, label: `#${a.id} ${a.name} (${a.slug})` }))}
          onChange={(v) => { setAppId(v); setPage(1); }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          style={{ width: 140 }}
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          options={[
            { value: '', label: '全部状态' },
            { value: 'paid', label: '已支付' },
            { value: 'pending', label: '待支付' },
            { value: 'refunded', label: '已退款' },
            { value: 'failed', label: '失败' },
          ]}
        />
        <Button onClick={load}>刷新</Button>
      </Space>

      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        pagination={{ current: page, pageSize: 20, total, onChange: setPage, showTotal: (t) => `共 ${t} 单` }}
        columns={[
          {
            title: '领取码 (out_trade_no)',
            dataIndex: 'out_trade_no',
            render: (v: string) => <Text code copyable>{v}</Text>,
          },
          { title: '手机号', dataIndex: 'phone', width: 130 },
          { title: '金额', dataIndex: 'amount_cents', width: 100, render: (c: number) => formatCNY(c) },
          { title: '渠道', dataIndex: 'channel', width: 100 },
          {
            title: '来源',
            dataIndex: 'from_ad',
            width: 90,
            render: (v: boolean) => (v ? <Tag color="purple">投放</Tag> : <Tag>自然</Tag>),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (s: string) => {
              const m = STATUS_TAG[s] ?? { color: 'default', label: s };
              return <Tag color={m.color}>{m.label}</Tag>;
            },
          },
          {
            title: '支付时间',
            dataIndex: 'paid_at',
            width: 170,
            render: (v?: string | null) => (v ? formatDateTime(v) : '-'),
          },
          {
            title: '操作',
            width: 100,
            render: (_: unknown, o: BundleOrderRow) =>
              o.status === 'paid' ? (
                <Popconfirm
                  title={`确认全额退款 ${formatCNY(o.amount_cents)}?`}
                  description="银商网关真实退钱, 订单转已退款; 已发放权益需人工评估回收。"
                  okText="退款"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => doRefund(o)}
                >
                  <Button size="small" danger loading={refunding === o.out_trade_no}>
                    退款
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]}
      />
    </Space>
  );
}
